// Qué conexiones son de una fila de «Canales por agente», y cuáles no.
//
// La pantalla asigna un agente por fila, y una fila puede ser un canal entero o
// un número concreto de ese canal. Confundirlos tiene una consecuencia muy
// concreta: asignar el agente del canal borraba el de cada número, en silencio.

import type { AgentConnection } from '@/types/agent'

/** El número que reclama una conexión, o cadena vacía si no reclama ninguno. */
export function connectionNumber(connection: Pick<AgentConnection, 'config'>): string {
  const config = connection.config as Record<string, unknown> | null | undefined
  return String(config?.phone_number_id ?? '').trim()
}

/**
 * The portal avatar has three names: the catalogue publishes `Avatar`, the portal's
 * connection uses `liveavatar` and the backend also maps `heygen`. They are one channel.
 */
const AVATAR_SOURCE_ALIASES = new Set(['avatar', 'liveavatar', 'heygen'])
export const AVATAR_CANONICAL_KEY = 'avatar'

/**
 * The single normalisation for channel keys on this screen. Rows and connections must be
 * indexed with the same one: from 2026-09-07 to 2026-10-19 the screen indexed connections
 * here without the avatar aliases and looked them up as `avatar`, so the avatar row
 * (and its on/off switch) silently disappeared.
 */
export function normalizeSourceKey(value: string | null | undefined): string {
  const normalized = String(value ?? '').trim().toLowerCase()
  return AVATAR_SOURCE_ALIASES.has(normalized) ? AVATAR_CANONICAL_KEY : normalized
}

/**
 * La clave con la que se indexa una fila: el canal, y el número si lo hay.
 *
 * Sin el número, dos filas del mismo canal serían la misma y se pisarían.
 */
export function rowKey(sourceKey: string, phoneNumberId?: string): string {
  const base = normalizeSourceKey(sourceKey)
  return phoneNumberId ? `${base}#${phoneNumberId}` : base
}

/**
 * Si esta conexión ocupa el sitio de esta fila, y por tanto hay que retirarla
 * antes de poner otra.
 *
 * La fila de un número solo reclama ese número; la del canal, solo las que no
 * reclaman ninguno.
 */
export function connectionBelongsToRow(
  connection: Pick<AgentConnection, 'source_key' | 'config'>,
  sourceKey: string,
  phoneNumberId?: string,
): boolean {
  if (normalizeSourceKey(connection.source_key) !== normalizeSourceKey(sourceKey)) return false
  return connectionNumber(connection) === String(phoneNumberId ?? '').trim()
}
